/**
 * Square Web Payments SDK 統合コンポーネント
 * クレジットカード決済 + Apple Pay / Google Pay対応
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Apple as AppleIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// Square SDK状態
const SDK_STATUS = {
  NOT_LOADED: 'not_loaded',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
};

// 決済状態
const PAYMENT_STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
};

// 決済方法タブ
const PAYMENT_TAB = {
  CARD: 0,
  APPLE_PAY: 1,
  GOOGLE_PAY: 2,
};

const SquarePayment = ({
  amount,
  orderId,
  storeId,
  onSuccess,
  onError,
  onCancel,
  disabled = false,
  enableDigitalWallet = true, // Apple Pay/Google Pay有効化
}) => {
  const [sdkStatus, setSdkStatus] = useState(SDK_STATUS.NOT_LOADED);
  const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.IDLE);
  const [card, setCard] = useState(null);
  const [applePay, setApplePay] = useState(null);
  const [googlePay, setGooglePay] = useState(null);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  const [error, setError] = useState(null);
  const [squareConfig, setSquareConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(PAYMENT_TAB.CARD);
  const cardContainerRef = useRef(null);
  const paymentsRef = useRef(null);

  // Square設定を取得
  const fetchSquareConfig = useCallback(async () => {
    try {
      const response = await api.get(`/payments/square/config${storeId ? `?storeId=${storeId}` : ''}`);
      if (response.data.success && response.data.config.enabled) {
        setSquareConfig(response.data.config);
        return response.data.config;
      } else {
        setError('Square決済が設定されていません');
        setSdkStatus(SDK_STATUS.ERROR);
        return null;
      }
    } catch (err) {
      console.error('Square設定取得エラー:', err);
      setError('Square設定の取得に失敗しました');
      setSdkStatus(SDK_STATUS.ERROR);
      return null;
    }
  }, [storeId]);

  // Square SDKを読み込む
  const loadSquareSDK = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.Square) {
        resolve(window.Square);
        return;
      }

      const script = document.createElement('script');
      script.src = squareConfig?.environment === 'production'
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.async = true;
      script.onload = () => {
        if (window.Square) {
          resolve(window.Square);
        } else {
          reject(new Error('Square SDKの読み込みに失敗しました'));
        }
      };
      script.onerror = () => reject(new Error('Square SDKの読み込みに失敗しました'));
      document.head.appendChild(script);
    });
  }, [squareConfig?.environment]);

  // Paymentsオブジェクトを初期化
  const initializePayments = useCallback(async () => {
    if (!squareConfig) return;

    setSdkStatus(SDK_STATUS.LOADING);
    setError(null);

    try {
      const Square = await loadSquareSDK();

      const payments = Square.payments(squareConfig.applicationId, squareConfig.locationId);
      paymentsRef.current = payments;

      // カード入力フィールドを作成
      const cardInstance = await payments.card();
      await cardInstance.attach('#square-card-container');
      setCard(cardInstance);

      // Digital Wallet対応
      if (enableDigitalWallet) {
        // Apple Pay初期化
        try {
          const applePayRequest = payments.paymentRequest({
            countryCode: 'JP',
            currencyCode: 'JPY',
            total: {
              amount: String(amount),
              label: '合計',
            },
          });

          const applePayInstance = await payments.applePay(applePayRequest);
          setApplePay(applePayInstance);
          setApplePayAvailable(true);
        } catch (applePayError) {
          console.log('Apple Payは利用できません:', applePayError.message);
          setApplePayAvailable(false);
        }

        // Google Pay初期化
        try {
          const googlePayRequest = payments.paymentRequest({
            countryCode: 'JP',
            currencyCode: 'JPY',
            total: {
              amount: String(amount),
              label: '合計',
            },
          });

          const googlePayInstance = await payments.googlePay(googlePayRequest);
          await googlePayInstance.attach('#square-google-pay-container');
          setGooglePay(googlePayInstance);
          setGooglePayAvailable(true);
        } catch (googlePayError) {
          console.log('Google Payは利用できません:', googlePayError.message);
          setGooglePayAvailable(false);
        }
      }

      setSdkStatus(SDK_STATUS.LOADED);
    } catch (err) {
      console.error('Square初期化エラー:', err);
      setError(err.message || 'カード入力の初期化に失敗しました');
      setSdkStatus(SDK_STATUS.ERROR);
    }
  }, [squareConfig, loadSquareSDK, amount, enableDigitalWallet]);

  // 初期化
  useEffect(() => {
    const init = async () => {
      await fetchSquareConfig();
    };
    init();
  }, [fetchSquareConfig]);

  // 設定取得後にSDKを初期化
  useEffect(() => {
    if (squareConfig && sdkStatus === SDK_STATUS.NOT_LOADED) {
      initializePayments();
    }
  }, [squareConfig, sdkStatus, initializePayments]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (card) {
        card.destroy().catch(console.error);
      }
    };
  }, [card]);

  // 共通の決済処理
  const processPayment = async (token, walletType = 'card') => {
    try {
      const response = await api.post('/payments/square/process', {
        sourceId: token,
        amount,
        orderId,
        storeId,
        walletType,
      });

      if (response.data.success) {
        setPaymentStatus(PAYMENT_STATUS.SUCCESS);
        onSuccess?.({
          transactionId: response.data.transactionId,
          receiptUrl: response.data.receiptUrl,
          cardBrand: response.data.cardBrand,
          last4: response.data.last4,
          walletType,
        });
      } else {
        throw new Error(response.data.error || '決済処理に失敗しました');
      }
    } catch (err) {
      console.error('決済エラー:', err);
      setPaymentStatus(PAYMENT_STATUS.ERROR);
      setError(err.response?.data?.error || err.message || '決済処理に失敗しました');
      onError?.(err);
    }
  };

  // カード決済処理
  const handleCardPayment = async () => {
    if (!card || paymentStatus === PAYMENT_STATUS.PROCESSING) return;

    setPaymentStatus(PAYMENT_STATUS.PROCESSING);
    setError(null);

    try {
      const result = await card.tokenize();

      if (result.status !== 'OK') {
        const errorMessage = result.errors?.map(e => e.message).join(', ') || 'カード情報の検証に失敗しました';
        throw new Error(errorMessage);
      }

      await processPayment(result.token, 'card');
    } catch (err) {
      console.error('カード決済エラー:', err);
      setPaymentStatus(PAYMENT_STATUS.ERROR);
      setError(err.message || 'カード決済に失敗しました');
      onError?.(err);
    }
  };

  // Apple Pay決済処理
  const handleApplePayPayment = async () => {
    if (!applePay || paymentStatus === PAYMENT_STATUS.PROCESSING) return;

    setPaymentStatus(PAYMENT_STATUS.PROCESSING);
    setError(null);

    try {
      const result = await applePay.tokenize();

      if (result.status !== 'OK') {
        throw new Error('Apple Pay認証に失敗しました');
      }

      await processPayment(result.token, 'apple_pay');
    } catch (err) {
      console.error('Apple Pay決済エラー:', err);
      setPaymentStatus(PAYMENT_STATUS.ERROR);
      setError(err.message || 'Apple Pay決済に失敗しました');
      onError?.(err);
    }
  };

  // Google Pay決済処理
  const handleGooglePayPayment = async () => {
    if (!googlePay || paymentStatus === PAYMENT_STATUS.PROCESSING) return;

    setPaymentStatus(PAYMENT_STATUS.PROCESSING);
    setError(null);

    try {
      const result = await googlePay.tokenize();

      if (result.status !== 'OK') {
        throw new Error('Google Pay認証に失敗しました');
      }

      await processPayment(result.token, 'google_pay');
    } catch (err) {
      console.error('Google Pay決済エラー:', err);
      setPaymentStatus(PAYMENT_STATUS.ERROR);
      setError(err.message || 'Google Pay決済に失敗しました');
      onError?.(err);
    }
  };

  // リセット
  const handleReset = () => {
    setPaymentStatus(PAYMENT_STATUS.IDLE);
    setError(null);
  };

  // 決済成功表示
  if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" color="success.main" gutterBottom>
          決済完了
        </Typography>
        <Typography variant="body2" color="text.secondary">
          決済が正常に処理されました
        </Typography>
      </Box>
    );
  }

  const hasDigitalWallet = applePayAvailable || googlePayAvailable;

  return (
    <Box>
      {/* エラー表示 */}
      <Collapse in={!!error}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            paymentStatus === PAYMENT_STATUS.ERROR && (
              <Button color="inherit" size="small" onClick={handleReset}>
                再試行
              </Button>
            )
          }
        >
          {error}
        </Alert>
      </Collapse>

      {/* SDK読み込み中 */}
      {sdkStatus === SDK_STATUS.LOADING && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body2" color="text.secondary">
            決済方法を準備中...
          </Typography>
        </Box>
      )}

      {/* Digital Wallet対応時のタブ */}
      {sdkStatus === SDK_STATUS.LOADED && hasDigitalWallet && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab icon={<CreditCardIcon />} label="カード" />
            {applePayAvailable && <Tab icon={<AppleIcon />} label="Apple Pay" />}
            {googlePayAvailable && <Tab icon={<PhoneIcon />} label="Google Pay" />}
          </Tabs>
        </Box>
      )}

      {/* カード入力フォーム */}
      <Box
        id="square-card-container"
        ref={cardContainerRef}
        sx={{
          minHeight: 90,
          mb: 2,
          display: sdkStatus === SDK_STATUS.LOADED && activeTab === PAYMENT_TAB.CARD ? 'block' : 'none',
          '& iframe': {
            width: '100%',
          },
        }}
      />

      {/* Google Pay コンテナ */}
      <Box
        id="square-google-pay-container"
        sx={{
          display: sdkStatus === SDK_STATUS.LOADED && activeTab === PAYMENT_TAB.GOOGLE_PAY ? 'block' : 'none',
          mb: 2,
        }}
      />

      {/* Apple Pay説明 */}
      {sdkStatus === SDK_STATUS.LOADED && activeTab === PAYMENT_TAB.APPLE_PAY && applePayAvailable && (
        <Box sx={{ textAlign: 'center', py: 2, mb: 2 }}>
          <AppleIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            下の「Apple Payで支払う」ボタンを押すと、Touch IDまたはFace IDで認証できます
          </Typography>
        </Box>
      )}

      {/* 決済ボタン */}
      {sdkStatus === SDK_STATUS.LOADED && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={paymentStatus === PAYMENT_STATUS.PROCESSING}
            sx={{ flex: 1 }}
          >
            キャンセル
          </Button>

          {/* カード決済ボタン */}
          {activeTab === PAYMENT_TAB.CARD && (
            <Button
              variant="contained"
              onClick={handleCardPayment}
              disabled={disabled || paymentStatus === PAYMENT_STATUS.PROCESSING}
              startIcon={
                paymentStatus === PAYMENT_STATUS.PROCESSING ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CreditCardIcon />
                )
              }
              sx={{ flex: 2 }}
            >
              {paymentStatus === PAYMENT_STATUS.PROCESSING
                ? '処理中...'
                : `¥${amount?.toLocaleString()} を支払う`}
            </Button>
          )}

          {/* Apple Pay決済ボタン */}
          {activeTab === PAYMENT_TAB.APPLE_PAY && applePayAvailable && (
            <Button
              variant="contained"
              onClick={handleApplePayPayment}
              disabled={disabled || paymentStatus === PAYMENT_STATUS.PROCESSING}
              startIcon={
                paymentStatus === PAYMENT_STATUS.PROCESSING ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AppleIcon />
                )
              }
              sx={{
                flex: 2,
                bgcolor: '#000',
                '&:hover': { bgcolor: '#333' },
              }}
            >
              {paymentStatus === PAYMENT_STATUS.PROCESSING
                ? '処理中...'
                : 'Apple Payで支払う'}
            </Button>
          )}

          {/* Google Pay決済ボタン */}
          {activeTab === PAYMENT_TAB.GOOGLE_PAY && googlePayAvailable && (
            <Button
              variant="contained"
              onClick={handleGooglePayPayment}
              disabled={disabled || paymentStatus === PAYMENT_STATUS.PROCESSING}
              startIcon={
                paymentStatus === PAYMENT_STATUS.PROCESSING ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PhoneIcon />
                )
              }
              sx={{
                flex: 2,
                bgcolor: '#4285F4',
                '&:hover': { bgcolor: '#3367D6' },
              }}
            >
              {paymentStatus === PAYMENT_STATUS.PROCESSING
                ? '処理中...'
                : 'Google Payで支払う'}
            </Button>
          )}
        </Box>
      )}

      {/* SDKエラー時の代替手段 */}
      {sdkStatus === SDK_STATUS.ERROR && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <ErrorIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            カード決済が利用できません
          </Typography>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ mt: 1 }}
          >
            他の決済方法を選択
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SquarePayment;
